/**
 * useOnlineStatus — robust, self-healing connectivity status.
 *
 * Why this is not just `navigator.onLine`:
 *   `navigator.onLine` only reflects the OS network interface, NOT real
 *   reachability of our backend. Worse, browsers frequently MISS the `online`
 *   event after a transient blip / WiFi roam / laptop sleep-wake. With a naive
 *   event-only listener the app latches into "offline" and stays there —
 *   locking pages and routing writes to the offline path — until a HARD
 *   REFRESH re-reads `navigator.onLine`. That caused intermittent
 *   "transaction didn't go through (works after refresh)" failures across
 *   Capital Change, PO price generation, etc.
 *
 * This module:
 *   • Treats `navigator.onLine === false` as definitely offline.
 *   • When the browser claims online, VERIFIES with a lightweight `/api/health`
 *     ping (navigator.onLine === true does not guarantee the server is up).
 *   • Self-heals: re-verifies on `focus`, `visibilitychange`, the `online`
 *     event, AND a periodic poll — so a missed `online` event recovers within
 *     seconds without a hard refresh.
 *   • Single shared singleton (one poll, one set of listeners) that every
 *     consumer subscribes to, so the whole app shares one source of truth.
 */
import { useState, useEffect } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PING_URL = `${BACKEND}/api/health`;
const POLL_MS = 20000;     // background reconcile interval
const PING_TIMEOUT_MS = 5000;

let _isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let _started = false;
let _verifying = false;
const _subs = new Set();

function _emit() { _subs.forEach((fn) => { try { fn(_isOnline); } catch { /* ignore */ } }); }
function _set(v) { if (v !== _isOnline) { _isOnline = v; _emit(); } }

async function _verify() {
  // Browser is certain we're offline — trust it, no need to ping.
  if (typeof navigator !== 'undefined' && !navigator.onLine) { _set(false); return; }
  if (_verifying) return;
  _verifying = true;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    const res = await fetch(PING_URL, { method: 'GET', cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    _set(!!res.ok);
  } catch {
    // Ping failed. Don't hard-lock to offline on a single slow/failed ping
    // while the browser still reports online (avoids false offline during a
    // brief backend hiccup). If the browser also says offline, go offline.
    _set(typeof navigator !== 'undefined' && navigator.onLine ? _isOnline : false);
  } finally {
    _verifying = false;
  }
}

function _start() {
  if (_started || typeof window === 'undefined') return;
  _started = true;

  const goOnline = () => { _set(true); _verify(); };
  const goOffline = () => _set(false);
  const onVisible = () => { if (document.visibilityState === 'visible') _verify(); };

  window.addEventListener('online', goOnline);
  window.addEventListener('offline', goOffline);
  window.addEventListener('focus', _verify);
  document.addEventListener('visibilitychange', onVisible);

  // Recovery poll — reconciles a missed `online` event automatically.
  setInterval(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) { _set(false); return; }
    _verify();
  }, POLL_MS);
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(_isOnline);
  useEffect(() => {
    _start();
    _subs.add(setIsOnline);
    setIsOnline(_isOnline);
    _verify();   // correct any stale state immediately on mount
    return () => { _subs.delete(setIsOnline); };
  }, []);
  return isOnline;
}
